import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2, Phone, Mail, Users, FileText, CalendarIcon, MessageSquare } from "lucide-react";
import { TimePicker } from "@/components/ui/time-picker";
import { cn } from "@/lib/utils";
import { ActivityType, ACTIVITY_TYPES, ACTIVITY_OUTCOMES, DealActivity } from "@/types/dealTracker";
import { format, parseISO } from "date-fns";

const ICON_MAP: Record<string, React.ReactNode> = {
  Phone: <Phone className="h-3.5 w-3.5" />,
  Mail: <Mail className="h-3.5 w-3.5" />,
  Users: <Users className="h-3.5 w-3.5" />,
  FileText: <FileText className="h-3.5 w-3.5" />,
  MessageSquare: <MessageSquare className="h-3.5 w-3.5" />,
};

interface LogActivityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (activity: {
    activity_type: ActivityType;
    title: string;
    activity_date: string;
    activity_time?: string;
    duration_minutes?: number;
    outcome?: string;
    notes?: string;
  }) => Promise<void>;
  isSubmitting?: boolean;
  initialData?: DealActivity | null;
}

const LogActivityModal = ({ open, onOpenChange, onSubmit, isSubmitting, initialData }: LogActivityModalProps) => {
  const [activityType, setActivityType] = useState<ActivityType>("phone_call");
  const [title, setTitle] = useState("");
  const [activityDate, setActivityDate] = useState<Date>(new Date());
  const [activityTime, setActivityTime] = useState(format(new Date(), "HH:mm"));
  const [duration, setDuration] = useState("");
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");

  const isEditing = !!initialData;

  useEffect(() => {
    if (open && initialData) {
      setActivityType(initialData.activity_type);
      setTitle(initialData.title);
      setActivityDate(parseISO(initialData.activity_date));
      setActivityTime(initialData.activity_time || format(new Date(), "HH:mm"));
      setDuration(initialData.duration_minutes?.toString() || "");
      setOutcome(initialData.outcome || "");
      setNotes(initialData.notes || "");
    }
  }, [open, initialData]);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    await onSubmit({
      activity_type: activityType,
      title: title.trim(),
      activity_date: format(activityDate, "yyyy-MM-dd"),
      activity_time: activityTime || undefined,
      duration_minutes: duration ? parseInt(duration) : undefined,
      outcome: outcome || undefined,
      notes: notes.trim() || undefined,
    });
    resetForm();
    onOpenChange(false);
  };

  const resetForm = () => {
    setActivityType("phone_call");
    setTitle("");
    setActivityDate(new Date());
    setActivityTime(format(new Date(), "HH:mm"));
    setDuration("");
    setOutcome("");
    setNotes("");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Activity' : 'Log Activity'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label>Activity Type</Label>
            <ToggleGroup type="single" value={activityType} onValueChange={(v) => v && setActivityType(v as ActivityType)} className="justify-start flex-wrap">
              {ACTIVITY_TYPES.map((t) => (
                <ToggleGroupItem key={t.value} value={t.value} className="text-xs px-2.5 gap-1">
                  {ICON_MAP[t.icon]}
                  {t.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Activity title" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !activityDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {activityDate ? format(activityDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={activityDate} onSelect={(d) => d && setActivityDate(d)} initialFocus className="p-3 pointer-events-auto" />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Time</Label>
              <TimePicker value={activityTime} onChange={setActivityTime} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Duration (min)</Label>
              <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="Minutes" />
            </div>
            <div className="space-y-2">
              <Label>Outcome</Label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {ACTIVITY_OUTCOMES.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" rows={2} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { resetForm(); onOpenChange(false); }}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={!title.trim() || !activityDate || isSubmitting}
              className="bg-[#ffd700] text-[#080c14] hover:bg-[#ffd700]/90"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {isEditing ? 'Update Activity' : 'Log Activity'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LogActivityModal;
